import { animate, AnimationEvent, style, transition, trigger } from '@angular/animations';
import { DOCUMENT, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { DataUtils } from '@iupics-util/tools/data.utils';
import { getViewport } from '@iupics/apiz-grid';
import { ZIndexUtils } from 'primeng/utils';

@Component({
  selector: 'iu-dropdown',
  standalone: true,
  imports: [NgStyle],
  animations: [
    trigger('showAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('.1s')]),
      transition(':leave', [animate('0s', style({ opacity: 0 }))]),
    ]),
  ],
  host: {
    '[attr.data-expanded]': 'open()',
    '[attr.data-align]': 'dropdownAlign()',
  },
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent {
  readonly #elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  readonly #document = inject(DOCUMENT);
  readonly #renderer = inject(Renderer2);

  readonly id = input<string | number>(DataUtils.uuid());
  readonly linkedTo = input<HTMLElement[]>([]);
  readonly open = model(false);

  #listeners: (() => void)[] = [];

  //#region Positioning
  itemDropdownEl: HTMLDivElement | undefined;
  readonly dropdownTop = signal<string | undefined>(undefined);
  readonly dropdownLeft = signal<string | undefined>(undefined);
  readonly dropdownWidth = signal<string | undefined>(undefined);
  readonly dropdownAlign = signal<'top' | 'bottom'>('bottom');
  readonly dropdownZIndex = signal<number>(0);

  readonly style = computed(() => ({
    zIndex: this.dropdownZIndex(),
    left: this.dropdownLeft(),
    width: this.dropdownWidth(),
    top: this.dropdownTop(),
  }));

  readonly close = output<void>();

  constructor() {
    effect(() => (this.open() ? this.addListeners() : this.destroy()));
  }

  toggle(value?: boolean): void {
    this.open.set(value ?? !this.open());
  }

  onShowAnimationStart(e: AnimationEvent): void {
    if (e.fromState === 'void' && e.toState === 'visible') {
      this.checkPosition(e.element);
    }
  }

  private checkPosition(div: HTMLDivElement): void {
    // Get the parameters
    const viewport = getViewport();
    const dropdownRect = div.getBoundingClientRect();
    const containerRect = this.#elementRef.nativeElement.parentElement.getBoundingClientRect();
    this.itemDropdownEl = div;

    // Handle z index
    ZIndexUtils.set('dropdown', div, 0);
    this.dropdownZIndex.set(ZIndexUtils.get(div));

    // move the overlay to body
    this.#renderer.appendChild(this.#document.body, this.itemDropdownEl);

    // if item overflow the window, set left to align with the right of the parent
    this.dropdownLeft.set(`${containerRect.left}px`);
    this.dropdownWidth.set(`${containerRect.width}px`);
    // Set the variable parameters
    // ? maybe there will be a bug but in our cases, it's not important
    if (viewport.height < containerRect.bottom + dropdownRect.height) {
      this.dropdownTop.set(`${containerRect.bottom - dropdownRect.height - containerRect.height}px`);
      this.dropdownAlign.set('top');
    } else {
      this.dropdownTop.set(`${containerRect.bottom}px`);
      this.dropdownAlign.set('bottom');
    }
  }

  private onKeyDownEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.toggle(false);
    }
  }

  private closeIfNotInScope(event: FocusEvent | MouseEvent): void {
    if (this.isInScope(event.target)) {
      return;
    }

    this.toggle(false);
  }

  private isInScope(target: EventTarget): boolean {
    return [this.itemDropdownEl, ...this.linkedTo()].some((el) => el?.contains(target as HTMLElement));
  }

  private addListeners(): void {
    this.#listeners.push(
      this.#renderer.listen('document', 'mousedown', (event) => this.closeIfNotInScope(event)),
      this.#renderer.listen('document', 'focusin', (event) => this.closeIfNotInScope(event)),
      this.#renderer.listen('document', 'keydown', (event) => this.onKeyDownEscape(event)),
      this.#renderer.listen('document', 'mousewheel', (event) => {
        if (this.itemDropdownEl.contains(event.target)) {
          return;
        }
        (this.#document.activeElement as HTMLElement)?.blur();
        this.toggle(false);
      }),
      this.#renderer.listen('window', 'resize', () => this.toggle(false)),
      this.#renderer.listen('window', 'blur', () => this.toggle(false))
    );
  }

  private clearListeners(): void {
    for (const unListen of this.#listeners) {
      unListen?.();
    }
    this.#listeners = [];
  }

  private destroy(): void {
    if (this.itemDropdownEl) {
      this.clearListeners();
      this.#renderer.removeChild(this.#document.body, this.itemDropdownEl);
      ZIndexUtils.clear(this.itemDropdownEl);
    }

    this.open.set(false);
    this.close.emit();
  }
}
